import { useEffect } from 'react';

/**
 * How much taller the form's content could be before it filled the page — the
 * empty band under the last field, on a form that fills its page (NetorcaForm
 * `fill`), where the fields scroll in a box of their own above the action bar.
 * Nothing on any other form, and nothing once the fields already scroll.
 */
export const pageSlack = (node: Element | null): number => {
    const box = node?.closest('.nf-form--fill')?.querySelector('.nf-scroll');
    const last = box?.lastElementChild;
    if (!box || !last) return 0;
    // the box's own bottom padding stays between the fields and the bar
    const padding = parseFloat(getComputedStyle(box).paddingBottom) || 0;
    const slack = box.getBoundingClientRect().bottom - padding - last.getBoundingClientRect().bottom;
    return Math.max(0, Math.floor(slack));
};

/**
 * Hands `grow` the page's slack once, when the control arrives. Two frames
 * late, so the fields around it have been laid out and what is measured is the
 * page as it will be read, not as it is half-way through mounting.
 *
 * Once, not on every resize: after that the height belongs to the reader, who
 * can drag it, and a page that took it back would be fighting them.
 */
export const useFillPage = (getNode: () => Element | null | undefined, grow: (slack: number) => void) => {
    useEffect(() => {
        let frame = window.requestAnimationFrame(() => {
            frame = window.requestAnimationFrame(() => {
                const slack = pageSlack(getNode() ?? null);
                if (slack > 0) grow(slack);
            });
        });
        return () => window.cancelAnimationFrame(frame);
        // once per mount, by design — see above
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
